import { useOutletContext, useSearchParams } from 'react-router-dom'

import {
  Box,
  Button,
  Card,
  EnhancedDimension,
  Tag,
  Text,
} from '@factorialco/deprecated-design-system'
import { PerformanceReviewOverviewPeerAssignmentTaskDocument } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { PeerAssignment } from 'modules/performance_process/components/overview/peerAssignment'
import { ReviewOverviewOutletContext } from 'modules/performance_process/pages/reviews/show/overview'

interface Props {
  minWidth?: EnhancedDimension
}

export const PeerAssignmentTask = ({ minWidth }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isSidepanelOpen = searchParams.get('peerAssignmentPanel') === 'open'
  const context = useOutletContext<ReviewOverviewOutletContext>()

  const { data, loading } = useQuery(PerformanceReviewOverviewPeerAssignmentTaskDocument, {
    variables: { id: context.reviewProcess.id },
  })

  if (loading) return null

  const targetsForPeersAssignmentCount = data?.performance.targetsForPeersAssignment.totalCount ?? 0
  const targetsWithoutPeersAssignedCount =
    data?.performance.targetsWithoutPeersAssigned.totalCount ?? 0

  const targetsWithPeersAssignedCount =
    targetsForPeersAssignmentCount - targetsWithoutPeersAssignedCount

  const isCompleted = targetsForPeersAssignmentCount === targetsWithPeersAssignedCount

  const onClick = () => setSearchParams({ peerAssignmentPanel: 'open' })

  if (targetsForPeersAssignmentCount === 0) return null

  return (
    <Box minWidth={minWidth} width={minWidth}>
      <Card onClick={onClick}>
        <Card.Title
          size="small"
          action={
            !isCompleted && (
              <Tag leftItem="circle" inline capitalize type="tangerine">
                {i18n.t('performance_process.overview.tasks.pending_tasks', {
                  count: targetsWithoutPeersAssignedCount,
                })}
              </Tag>
            )
          }
        >
          {i18n.t('performance_process.overview.tasks.peer_assignment.title')}
        </Card.Title>
        <Card.Content>
          <Box gap="s16" width="full" flexGrow justifyContent="spaceBetween">
            <Text.Normal color="grey1000">
              {i18n.t('performance_process.overview.tasks.peer_assignment.description')}
            </Text.Normal>
            <Button onClick={onClick} type="secondary">
              {isCompleted
                ? i18n.t('performance_process.overview.tasks.view_all')
                : i18n.t('performance_process.overview.tasks.peer_assignment.action')}
            </Button>
          </Box>
        </Card.Content>
      </Card>

      <Sidepanel
        open={isSidepanelOpen}
        onClose={() => setSearchParams({})}
        width="wideXL"
        title={i18n.t('performance_process.reviews.peer_management.peer_assignments_title')}
      >
        <PeerAssignment
          targetsForPeerAssignmentCount={targetsForPeersAssignmentCount}
          pendingTargetsForPeerAssignmentCount={targetsWithoutPeersAssignedCount}
          canAssign={context.reviewProcess.status === 'active'}
        />
      </Sidepanel>
    </Box>
  )
}
